import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components';
import { AuthLayout } from './AuthLayout';

const MIN_LENGTH = 8;

export interface ResetPasswordProps {
  /** True once Supabase has established a session from the email link. */
  ready: boolean;
  /** Which link brought them here. Changes the wording, not the behaviour. */
  mode: 'recovery' | 'invite';
  /** Called after the password is changed; the caller leaves this flow. */
  onDone: () => void;
}

/** Both links end here, but "reset" is wrong for someone who never had one. */
const COPY = {
  recovery: {
    eyebrow: 'Reset password',
    title: 'Choose a new password',
    lede: "You'll be signed in as soon as it's saved.",
    expiredTitle: 'That link is no longer valid',
    expiredLede: 'Reset links can only be used once, and they expire an hour after being sent.',
  },
  invite: {
    eyebrow: 'Welcome',
    title: 'Set your password',
    lede: 'Choose a password to finish setting up your account.',
    expiredTitle: 'That invite is no longer valid',
    expiredLede: 'Invite links can only be used once, and they expire after being sent.',
  },
} as const;

/**
 * Second half of the recovery and invite flows. Reachable only with a session,
 * which Supabase creates from the emailed link. Without one there is nothing
 * to update, so we send the user back rather than showing a form that cannot work.
 */
export const ResetPassword: React.FC<ResetPasswordProps> = ({ ready, mode, onDone }) => {
  const copy = COPY[mode];
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Those two passwords do not match.');
      return;
    }

    setBusy(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setError(updateError.message);
      setBusy(false);
      return;
    }
    // The link's session is now a normal session, so leaving this flow drops
    // the caller straight into the workspace.
    onDone();
  }

  if (!ready) {
    return (
      <AuthLayout
        eyebrow="Link expired"
        title={copy.expiredTitle}
        lede={copy.expiredLede}
        footer={<Link to="/auth/sign-in">Back to sign in</Link>}
      >
        <div className="auth__notice">
          {mode === 'invite' ? (
            <>Ask an administrator to send a new invite.</>
          ) : (
            <>
              Request a fresh link from <Link to="/auth/forgot-password">Forgot password</Link>.
            </>
          )}
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout eyebrow={copy.eyebrow} title={copy.title} lede={copy.lede}>
      <form className="auth__form" onSubmit={onSubmit}>
        <div className="auth__field">
          <label className="field-label" htmlFor="password">
            {mode === 'invite' ? 'Password' : 'New password'}
          </label>
          <input
            id="password"
            type="password"
            className="text-input"
            value={password}
            autoComplete="new-password"
            autoFocus
            required
            minLength={MIN_LENGTH}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="auth__hint">At least {MIN_LENGTH} characters.</div>
        </div>

        <div className="auth__field">
          <label className="field-label" htmlFor="confirm">
            Confirm password
          </label>
          <input
            id="confirm"
            type="password"
            className="text-input"
            value={confirm}
            autoComplete="new-password"
            required
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        {error && (
          <div className="auth__error" role="alert">
            {error}
          </div>
        )}

        <Button type="submit" variant="primary" fullWidth loading={busy}>
          Save password
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
